/**
 * OMON-129 (single-plan transcript scope): the Combined narrative tab.
 *
 * One chronological read of everything the carers recorded for the current
 * plan period — both streams. Decisions (Ed, 2026-06-10, mock-verified):
 * - OLDEST day first: it reads like a story building toward today.
 * - Notes are the substance: full text, never truncated.
 * - Recordings WITHOUT notes render as one-line "quiet markers" (consecutive
 *   same-fact+carer runs collapse to "×n") so the rhythm of care stays
 *   visible between notes.
 *
 * This is a read-projection over `state.patientNarrative` (built by
 * `loadPatientActuals` from the same `_observation` snapshot the charts
 * use) — nothing new is stored. The later OMON-129 scopes (cross-caseload
 * feed, filters, flagged/ack via ADR-0020 Task) build on top of this.
 */

import { state } from "../state";
import { escapeHtml } from "../utils/html";
import { localDayKey, localTimeOf } from "../utils/time";
import type { NarrativeEntry } from "../types";
import { isNotApplicable, notApplicableLabel, notApplicableReason } from "./not-applicable";

// ---------------------------------------------------------------------
// Pure grouping core (unit-tested in narrative.test.ts)
// ---------------------------------------------------------------------

/**
 * Project a single `monitored-event` `_observation` doc into a NarrativeEntry,
 * across every recording regime plus note-only entries:
 *   - boolean     → `valueBoolean`         (kind "state", "yes"/"no")
 *   - rating/tally→ `valueInteger`         (kind "tally", number carried elsewhere)
 *   - note-only   → no `value[x]`, a `note` (kind "tally", the note carries it)
 *   - ADR-0035 am.9 → `dataAbsentReason` not-applicable (kind "not-applicable",
 *     the reason label + optional note; never a value)
 *
 * Returns `null` when the doc carries nothing to show (no value AND no note) —
 * e.g. a malformed or empty observation. Extracted from `loadPatientActuals`
 * so this branching (notably the boolean case, previously dropped) is
 * unit-testable without a Firestore snapshot.
 */
export function monitoredEventNarrativeEntry(
  data: Record<string, any>,
  code: string,
): NarrativeEntry | null {
  const performerName = Array.isArray(data.performer) ? data.performer[0]?.display : undefined;
  // ADR-0035 am.9b: checked FIRST — the answer carries no value[x], and must
  // never fall through to the note-only branch, where it would read as a
  // recording of the item.
  if (isNotApplicable(data)) {
    const reason = notApplicableReason(data);
    const naNote = Array.isArray(data.note) && data.note.length > 0 ? data.note[0]?.text : undefined;
    return {
      date: data.effectiveDateTime ?? "",
      code,
      kind: "not-applicable",
      notApplicableText: reason.text,
      notApplicableReason: reason.code,
      performerName,
      note: typeof naNote === "string" && naNote.trim() ? naNote : undefined,
    };
  }
  const boolVal = typeof data.valueBoolean === "boolean" ? (data.valueBoolean as boolean) : undefined;
  // A decimal number (carer 1.5.4+: £12.50, 62.4 kg) arrives as valueQuantity.
  const num: number | undefined = typeof data.valueInteger === "number"
    ? data.valueInteger
    : typeof data.valueQuantity?.value === "number" ? data.valueQuantity.value : undefined;
  const hasInt = data.valueInteger != null || num !== undefined;
  const noteText = Array.isArray(data.note) && data.note.length > 0 ? data.note[0].text : undefined;

  let state: string | undefined;
  let kind: "state" | "tally";
  if (boolVal !== undefined) {
    state = boolVal ? "yes" : "no";
    kind = "state";
  } else if (hasInt || noteText) {
    // rating/tally number, or a note-only entry — no chip, the note carries.
    kind = "tally";
  } else {
    return null; // nothing to show
  }

  return {
    date: data.effectiveDateTime ?? "",
    code,
    kind,
    state,
    value: num,
    performerName,
    note: noteText,
  };
}

export interface NarrativeQuietRun {
  kind: "quiet";
  /** Time of the FIRST recording in the run (ISO). */
  date: string;
  code: string;
  count: number;
  performerName?: string;
  /**
   * The recordings themselves, so the renderer can show WHAT WAS RECORDED.
   *
   * ☠️ Ed, 2026-09-02: a rating or a yes/no entered without a note rendered as
   * "recorded (no note)" — the value was collected, stored, charted, and then
   * dropped on the floor by the one view a planner reads to find out what
   * happened. Folding into a bare `count` was right for a tally (whose value is
   * the sentinel 1) and wrong for everything else.
   *
   * `count` stays: for tallies it is still the only meaningful summary.
   */
  entries: NarrativeEntry[];
}

export interface NarrativeNoteItem {
  kind: "note";
  entry: NarrativeEntry;
}

export type NarrativeItem = NarrativeQuietRun | NarrativeNoteItem;

export interface NarrativeDay {
  /** YYYY-MM-DD */
  day: string;
  noteCount: number;
  recordingCount: number;
  items: NarrativeItem[];
}

/**
 * Group sorted entries into days (NEWEST day first, entries within a day left
 * in time order), folding consecutive
 * noteless same-fact+carer recordings into quiet runs. Entries on or after
 * `fromDate` (ISO, optional) only — the current plan period.
 */
export function buildNarrativeDays(
  entries: NarrativeEntry[],
  fromDate?: string,
): NarrativeDay[] {
  const days = new Map<string, NarrativeDay>();
  const sorted = [...entries]
    .filter((e) => e.date && (!fromDate || e.date >= fromDate))
    .sort((a, b) => a.date.localeCompare(b.date));

  for (const e of sorted) {
    const dayKey = localDayKey(e.date);
    let day = days.get(dayKey);
    if (!day) {
      day = { day: dayKey, noteCount: 0, recordingCount: 0, items: [] };
      days.set(dayKey, day);
    }
    // An ADR-0035 am.9 not-applicable answer ANSWERS the item, so it counts
    // as a recording like any other.
    day.recordingCount += 1;
    const noteText = e.note?.trim();
    if (noteText) {
      day.noteCount += 1;
      day.items.push({ kind: "note", entry: e });
      continue;
    }
    // am.9: a not-applicable answer is never folded into a quiet run — it is
    // a statement about the shift, not one more tick of the item, and a run
    // promises every entry shares one recording kind.
    if (e.kind === "not-applicable") {
      day.items.push({ kind: "note", entry: e });
      continue;
    }
    const last = day.items[day.items.length - 1];
    if (
      last && last.kind === "quiet" &&
      last.code === e.code && last.performerName === e.performerName
    ) {
      last.count += 1;
      last.entries.push(e);
    } else {
      day.items.push({
        kind: "quiet",
        date: e.date,
        code: e.code,
        count: 1,
        performerName: e.performerName,
        entries: [e],
      });
    }
  }
  // Newest DAY first — reading a plan in progress, the recent end is the end
  // you want, and otherwise every visit means scrolling to the bottom.
  // Entries WITHIN a day stay chronological, so a day still reads as the story
  // of that shift: morning, then afternoon, then evening.
  return [...days.values()].reverse();
}

// ---------------------------------------------------------------------
// Rendering
// ---------------------------------------------------------------------

const STATE_LABELS: Record<string, string> = {
  // boolean regime (ADR-0022 carer recording)
  yes: "Yes",
  no: "No",
};

/**
 * The recorded value as {text, background, foreground}, or null when the doc
 * carries nothing a human would want to read.
 *
 * ☠️ Null is NOT "no value" — it is "no value worth showing". A tally's
 * `valueInteger` is the sentinel 1, so rendering it would read as a score of
 * one. That distinction is why this returns null rather than a "Recorded"
 * string: the caller needs to know the difference between "she recorded 0/5"
 * and "she pressed the tally button", and a fallback label hides it.
 */
export function valueParts(
  e: NarrativeEntry,
): { text: string; bg: string; fg: string } | null {
  // am.9b: never a value — rendered by notApplicableRow instead.
  if (e.kind === "not-applicable") return null;
  if (e.kind === "score" && typeof e.score === "number") {
    return { text: `${e.score} / 5`, bg: "#f5f3ff", fg: "#7c3aed" };
  }
  if (e.kind === "state" && e.state) {
    return { text: STATE_LABELS[e.state] || e.state, bg: "#fffbeb", fg: "#92400e" };
  }
  const recordingKind = (state.catalogueData.get(e.code) as { _recordingKind?: string } | undefined)?._recordingKind;
  if (recordingKind === "rating" && typeof e.value === "number") {
    return { text: `${e.value}`, bg: "#eff6ff", fg: "#1e40af" };
  }
  return null;
}

function chip(text: string, bg: string, fg: string, size = "11px"): string {
  return `<span style="font-size:${size};font-weight:600;border-radius:8px;padding:1px 8px;margin-left:6px;background:${bg};color:${fg};">${escapeHtml(text)}</span>`;
}

function valueChip(e: NarrativeEntry): string {
  const v = valueParts(e);
  // A note-bearing row still says "Recorded" when the fact has no readable
  // value (a tally), because the row already has the note as its substance.
  return v ? chip(v.text, v.bg, v.fg) : chip("Recorded", "#f0fdf4", "#15803d");
}

/**
 * The value chips for a noteless run — one per recording that has something to
 * show, in the order they were recorded.
 *
 * Empty for a tally run (nothing readable), which is exactly when the "×N"
 * suffix carries the meaning instead.
 */
function quietValues(run: NarrativeQuietRun): string {
  return run.entries
    .map((e) => valueParts(e))
    .filter((v): v is { text: string; bg: string; fg: string } => v !== null)
    .map((v) => chip(v.text, v.bg, v.fg, "10px"))
    .join("");
}

/**
 * What to say after the chips. A run with readable values needs no epitaph —
 * the values ARE the content. Only a run with nothing to show keeps the old
 * "recorded (no note)" wording, and only a multi-recording run needs the ×N.
 */
function quietSuffix(run: NarrativeQuietRun): string {
  // A run is one fact by one carer, so every entry shares a recording kind:
  // either they all have a readable value or none do. `some` is therefore the
  // whole question, and there is no mixed case to reason about.
  if (run.entries.some((e) => valueParts(e) !== null)) return "";
  return `${run.count > 1 ? ` ×${run.count}` : ""} — recorded (no note)`;
}

function factTitle(code: string): string {
  return state.catalogueData.get(code)?.title || code;
}

function timeOf(iso: string): string {
  return localTimeOf(iso);
}

const DAY_NAMES = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const MONTH_NAMES = ["January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"];

// ── Year › month › week › day folding (Ed, 2026-10-14) ───────────────────
// Clients run for years, so a flat day list is years of scrolling. Days
// nest into Monday-to-Sunday weeks, weeks into months, months into years.
// A week that crosses a month boundary appears in both months, each part
// holding only that month's days, so every day lives in exactly one place.

export interface NarrativeWeek {
  /** YYYY-MM-DD of the week's Monday (the key, even when clipped). */
  monday: string;
  days: NarrativeDay[];
  noteCount: number;
  recordingCount: number;
}
export interface NarrativeMonth {
  /** YYYY-MM */
  month: string;
  weeks: NarrativeWeek[];
  noteCount: number;
  recordingCount: number;
}
export interface NarrativeYear {
  year: string;
  months: NarrativeMonth[];
  noteCount: number;
  recordingCount: number;
}

function mondayOf(dayKey: string): string {
  const d = new Date(`${dayKey}T12:00:00`);
  const back = (d.getDay() + 6) % 7; // Mon=0 … Sun=6
  d.setDate(d.getDate() - back);
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/** Nest days (newest first, as buildNarrativeDays returns them) into years,
 *  months and weeks, newest first at every level, with totals at each. */
export function buildNarrativeTree(days: NarrativeDay[]): NarrativeYear[] {
  const years: NarrativeYear[] = [];
  for (const day of days) {
    const y = day.day.slice(0, 4);
    const m = day.day.slice(0, 7);
    const w = mondayOf(day.day);
    let year = years[years.length - 1];
    if (!year || year.year !== y) years.push((year = { year: y, months: [], noteCount: 0, recordingCount: 0 }));
    let month = year.months[year.months.length - 1];
    if (!month || month.month !== m) year.months.push((month = { month: m, weeks: [], noteCount: 0, recordingCount: 0 }));
    let week = month.weeks[month.weeks.length - 1];
    if (!week || week.monday !== w) month.weeks.push((week = { monday: w, days: [], noteCount: 0, recordingCount: 0 }));
    week.days.push(day);
    for (const t of [week, month, year]) {
      t.noteCount += day.noteCount;
      t.recordingCount += day.recordingCount;
    }
  }
  return years;
}

/** "6–12 Oct", clipped to the days actually inside this month's part. */
function weekHeading(week: NarrativeWeek): string {
  const first = new Date(`${week.days[week.days.length - 1].day}T12:00:00`);
  const last = new Date(`${week.days[0].day}T12:00:00`);
  const mon = (d: Date) => MONTH_NAMES[d.getMonth()].slice(0, 3);
  return first.getTime() === last.getTime()
    ? `Week · ${first.getDate()} ${mon(first)}`
    : `Week · ${first.getDate()}–${last.getDate()} ${mon(last)}`;
}

function monthHeading(month: string): string {
  return `${MONTH_NAMES[Number(month.slice(5, 7)) - 1]} ${month.slice(0, 4)}`;
}

function counts(t: { noteCount: number; recordingCount: number }): string {
  return `· ${t.noteCount} note${t.noteCount === 1 ? "" : "s"} · ${t.recordingCount} recording${t.recordingCount === 1 ? "" : "s"}`;
}

function dayHeading(dayKey: string): string {
  const d = new Date(`${dayKey}T12:00:00`);
  if (isNaN(d.getTime())) return dayKey;
  return `${DAY_NAMES[d.getDay()]} ${d.getDate()} ${MONTH_NAMES[d.getMonth()]}`;
}

/** Render the Combined narrative tab's content HTML.
 *
 *  `foldable` (Tali, Ed 2026-10-14: "make dates foldable"): days fold into
 *  weeks, weeks into months, months into years, each a native <details>
 *  with its totals; the newest year, month, week and day start open and
 *  everything older starts folded. Off by default, because the downloadable
 *  plan copy uses this too and a printed or emailed copy must show every
 *  day. */
export function renderCombinedNarrative(opts: { foldable?: boolean } = {}): string {
  const plan = state.patientCarePlans[0];
  const days = buildNarrativeDays(state.patientNarrative, plan?.periodStart);

  if (days.length === 0) {
    return `<div class="empty-state" style="padding:24px;">
      <h3>Nothing recorded yet</h3>
      <p>Carer notes and recordings for this plan will read here as one chronological story.</p>
    </div>`;
  }

  let html = `<p style="font-size:13px;color:#6b7280;margin-bottom:4px;">Everything the carers wrote for this plan, newest day first. Recordings without text show as quiet markers so the rhythm of care stays visible.</p>`;

  const dayHead = (day: NarrativeDay) => `
        ${escapeHtml(dayHeading(day.day))}
        <span style="color:#9ca3af;font-weight:500;letter-spacing:0;text-transform:none;">${counts(day)}</span>`;
  const headStyle = "font-size:11px;font-weight:700;letter-spacing:.06em;text-transform:uppercase;color:#991b1b;margin:16px 0 4px;display:flex;align-items:baseline;gap:8px;";

  if (opts.foldable !== true) {
    for (const day of days) html += `<div style="${headStyle}">${dayHead(day)}</div>${dayItemsHtml(day)}`;
    return html;
  }

  // Folded: year › month › week › day. Only the newest path starts open.
  const fold = (cls: string, open: boolean, summary: string, body: string, indent: number) =>
    `<details class="narrative-fold ${cls}"${open ? " open" : ""} style="margin-left:${indent}px;">` +
    `<summary style="cursor:pointer;padding:4px 0;">${summary}</summary>${body}</details>`;
  const levelHead = (label: string, t: { noteCount: number; recordingCount: number }, size: number) =>
    `<span style="font-size:${size}px;font-weight:700;color:#111827;">${escapeHtml(label)}</span> ` +
    `<span style="font-size:13px;color:#6b7280;">${counts(t)}</span>`;
  html += `<p style="font-size:13px;color:#6b7280;margin-bottom:4px;">Click a year, month, week or day to open or fold it.</p>`;
  buildNarrativeTree(days).forEach((year, yi) => {
    const monthsHtml = year.months.map((month, mi) => {
      const weeksHtml = month.weeks.map((week, wi) => {
        const daysHtml = week.days.map((day, di) => {
          const open = yi === 0 && mi === 0 && wi === 0 && di === 0;
          return `<details class="narrative-fold narrative-day"${open ? " open" : ""} style="margin-left:16px;">` +
            `<summary style="${headStyle}cursor:pointer;">${dayHead(day)}</summary>${dayItemsHtml(day)}</details>`;
        }).join("");
        return fold("narrative-week", yi === 0 && mi === 0 && wi === 0, levelHead(weekHeading(week), week, 13), daysHtml, 16);
      }).join("");
      return fold("narrative-month", yi === 0 && mi === 0, levelHead(monthHeading(month.month), month, 15), weeksHtml, 16);
    }).join("");
    html += fold("narrative-year", yi === 0, levelHead(year.year, year, 17), monthsHtml, 0);
  });
  return html;
}

/**
 * ADR-0035 am.9b: a carer's "Not applicable on this shift" answer, as one
 * muted line: "{Fact} · Not applicable — {reason} · {carer}", and the carer's
 * note (if any) beneath in the same muted voice. No value chip: it is an
 * absence, and must not read like one more recording of the item.
 */
export function notApplicableRow(e: NarrativeEntry, factTitle: string, time: string): string {
  return `
        <div class="narrative-na" style="display:flex;gap:12px;padding:6px 4px;border-top:1px solid #f3f4f6;">
          <div style="width:52px;flex:0 0 auto;font-size:13px;color:#9ca3af;font-variant-numeric:tabular-nums;">${escapeHtml(time)}</div>
          <div style="flex:1;font-size:13px;color:#6b7280;font-style:italic;">${escapeHtml(factTitle)} · ${escapeHtml(notApplicableLabel(e.notApplicableText))} · ${escapeHtml(e.performerName || "—")}${e.note?.trim()
            ? `<div style="font-size:13px;color:#6b7280;margin-top:4px;white-space:pre-wrap;">${escapeHtml(e.note)}</div>`
            : ""}</div>
        </div>`;
}

/** One day's entries: notes in full, noteless runs as quiet markers. */
function dayItemsHtml(day: NarrativeDay): string {
  let html = "";
  for (const item of day.items) {
    if (item.kind === "note" && item.entry.kind === "not-applicable") {
      html += notApplicableRow(item.entry, factTitle(item.entry.code), timeOf(item.entry.date));
    } else if (item.kind === "note") {
      const e = item.entry;
      html += `
        <div style="display:flex;gap:12px;padding:8px 4px;border-top:1px solid #f3f4f6;">
          <div style="width:52px;flex:0 0 auto;font-size:13px;color:#6b7280;font-variant-numeric:tabular-nums;padding-top:1px;">${escapeHtml(timeOf(e.date))}</div>
          <div style="flex:1;">
            <div style="font-size:13px;"><b style="font-weight:650;">${escapeHtml(factTitle(e.code))}</b>${valueChip(e)} <span style="color:#6b7280;">· ${escapeHtml(e.performerName || "—")}</span></div>
            <div style="font-size:13px;color:#374151;line-height:1.55;margin-top:6px;background:#f9fafb;border-left:3px solid #ddd6fe;border-radius:6px;padding:8px 12px;white-space:pre-wrap;">${escapeHtml(e.note || "")}</div>
          </div>
        </div>`;
    } else {
      html += `
        <div style="display:flex;gap:12px;padding:6px 4px;border-top:1px solid #f3f4f6;">
          <div style="width:52px;flex:0 0 auto;font-size:13px;color:#6b7280;font-variant-numeric:tabular-nums;">${escapeHtml(timeOf(item.date))}</div>
          <div style="font-size:11px;color:#9ca3af;font-style:italic;">${escapeHtml(factTitle(item.code))}${quietValues(item)}${quietSuffix(item)} · ${escapeHtml(item.performerName || "—")}</div>
        </div>`;
    }
  }
  return html;
}
